import useWbsImportMutation from "@/api/mutation/userWBSImportMutaion";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";
import { errorConfig } from "@/lib/toastError";
import { t } from "i18next";
import { useRef } from "react";
import { toast } from "react-toastify";
import { Button } from "../ui/button";
import DownLoadIcon from "@/assets/svg/DownLoad.svg";
import ExcelIcon from "@/assets/v2/svg/excel.svg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import useProjectQuery from "@/api/query/useProjectQuery";
import { useUser } from "@/hooks/useUser";
import { UserRoleEnumValue } from "@backend/src/schemas/enums";
import { Task } from "@/api/mutation/useTaskCreateMutation";
import Loader from "../common/Loader";
import ExcelJS from "exceljs";
import useProjectDetail from "@/api/query/useProjectDetailQuery";
import dateFormater from "@/helperFuntions/dateFormater";

type Props = {
  projectId: string;
  refetch: () => void;
  iconOnly?: boolean;
  newTask: (data: Task[]) => void;
};

function WbsImport(props: Props) {
  const labelStyle = "font-medium text-base text-gray-450/30";
  const wbsImportMutation = useWbsImportMutation(props.projectId);
  const csvUplodeRef = useRef<HTMLInputElement>(null);
  const uploadCsv = () => {
    if (
      csvUplodeRef.current &&
      csvUplodeRef.current.files &&
      csvUplodeRef?.current?.files[0]
    ) {
      const formData = new FormData();
      formData.append("csv", csvUplodeRef?.current?.files[0]);
      wbsImportMutation.mutate(formData, {
        onSuccess(data) {
          toast.success(data.data.message);
          if (csvUplodeRef?.current) {
            csvUplodeRef.current.value = "";
          }
          props.newTask(data.data.data);
          props?.refetch();
        },
        onError(error) {
          toast.error(
            errorKeyFormate(error.response?.data.message + ""),
            errorConfig,
          );
          if (csvUplodeRef?.current) {
            csvUplodeRef.current.value = "";
          }
        },
      });
    }
  };
  const projectDetail = useProjectDetail(props.projectId, true);

  const downloadCsv = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Tasks");

    worksheet.columns = [
      { header: "ID", key: "id", width: 10 },
      { header: "Activites / Taches", key: "task", width: 40 },
      { header: "Description", key: "description", width: 50 },
      {
        header: "Successeurs (séparés par virgule)",
        key: "dependencies",
        width: 40,
      },
      { header: "Date de debut (dd/mm/yyyy)", key: "startDate", width: 25 },
      { header: "Duree (optionel)", key: "duration", width: 20 },
      { header: "Date de fin (dd/mm/yyyy)", key: "endDate", width: 25 },
      { header: "Assigné à", key: "assignedTo", width: 25 },
      { header: "Statut", key: "status", width: 18 },
      { header: "Progrès", key: "progress", width: 12 },
      { header: "Jalon", key: "milestone", width: 10 },
    ];

    const projectStartDateStr = projectDetail.data?.data.data.startDate;
    const projectStartDate = projectStartDateStr
      ? new Date(projectStartDateStr)
      : new Date();
    const futureDate = new Date(projectStartDate);
    futureDate.setDate(futureDate.getDate());
    const formattedStartDate = dateFormater(futureDate);

    const sampleTasks = [
      {
        id: "1",
        task: "Initialisation",
        description: "Phase d'initialisation du projet",
        dependencies: "2",
        startDate: "",
        duration: "",
        progress: "0",
      },
      {
        id: "1.1",
        task: "Etude d'opportunité et approbation",
        description: "Analyse de la faisabilité et validation du projet",
        dependencies: "2.9",
        startDate: "",
        duration: "",
        progress: "100",
      },
      {
        id: "1.2",
        task: "Préparation de la Charte Projet - Justification",
        description:
          "Rédaction de la charte projet avec justification financière et technique",
        dependencies: "2.2, 2.4",
        startDate: formattedStartDate,
        duration: "20",
        progress: "0",
      },
      {
        id: "1.3",
        task: "Estimation coûts/délais fonctionnels Finances",
        description:
          "Estimation des coûts et délais pour le département financier",
        dependencies: "2.10",
        startDate: formattedStartDate,
        duration: "15",
        progress: "0",
      },
      {
        id: "1.4",
        task: "Estimation coûts/délais fonctionnels Ventes",
        description:
          "Estimation des coûts et délais pour le département commercial",
        dependencies: "2.7",
        startDate: formattedStartDate,
        duration: "5",
        progress: "0",
      },
      {
        id: "1.5",
        task: "Estimation coûts/délais fonctionnels Achats",
        description:
          "Estimation des coûts et délais pour le département achats",
        dependencies: "2.8",
        startDate: formattedStartDate,
        duration: "2",
        progress: "0",
      },
      {
        id: "1.6",
        task: "Estimation coûts/délais fonctionnels Logistique",
        description:
          "Estimation des coûts et délais pour le département logistique",
        dependencies: "2.6",
        startDate: "",
        duration: "5",
        progress: "0",
      },
      {
        id: "1.7",
        task: "Estimation délais/ressources Infrastructure",
        description: "Planification des ressources infrastructure nécessaires",
        dependencies: "2.1",
        startDate: "",
        duration: "5",
        progress: "0",
      },
      {
        id: "1.8",
        task: "Estimation coûts/délais fonctionnels Production",
        description:
          "Estimation des coûts et délais pour le département production",
        dependencies: "2.5",
        startDate: "",
        duration: "5",
        progress: "0",
      },
      {
        id: "1.9",
        task: "Rédaction de la Charte Projet",
        description: "Finalisation et rédaction complète de la charte projet",
        dependencies: "2.5",
        startDate: "",
        duration: "10",
        progress: "0",
      },
      {
        id: "1.10",
        task: "Approbation de la Charte Projet",
        description: "Validation finale de la charte par les parties prenantes",
        dependencies: "2.3",
        startDate: "",
        duration: "1",
        progress: "0",
      },
      {
        id: "2",
        task: "Planification",
        description: "Phase de planification détaillée du projet",
        dependencies: "3",
        startDate: "",
        duration: "",
        progress: "0",
      },
      {
        id: "2.1",
        task: "Conception générale Finances",
        description:
          "Conception des processus et interfaces pour le département financier",
        dependencies: "",
        startDate: "",
        duration: "20",
        progress: "0",
      },
      {
        id: "2.2",
        task: "Conception générale Ventes",
        description:
          "Conception des processus et interfaces pour le département commercial",
        dependencies: "3.4",
        startDate: "",
        duration: "20",
        progress: "0",
      },
      {
        id: "2.3",
        task: "Conception générale Achats",
        description:
          "Conception des processus et interfaces pour le département achats",
        dependencies: "",
        startDate: "",
        duration: "20",
        progress: "0",
      },
      {
        id: "2.4",
        task: "Conception générale Logistique",
        description:
          "Conception des processus et interfaces pour le département logistique",
        dependencies: "",
        startDate: "",
        duration: "20",
        progress: "0",
      },
      {
        id: "2.5",
        task: "Conception générale Infrastructure",
        description: "Conception de l'architecture technique et infrastructure",
        dependencies: "3.3",
        startDate: "",
        duration: "20",
        progress: "0",
      },
      {
        id: "2.6",
        task: "Conception générale Production",
        description:
          "Conception des processus et interfaces pour le département production",
        dependencies: "3.5",
        startDate: "",
        duration: "20",
        progress: "0",
      },
      {
        id: "2.7",
        task: "Rédaction du cahier des charges",
        description:
          "Documentation détaillée des spécifications fonctionnelles et techniques",
        dependencies: "3.6",
        startDate: "",
        duration: "22",
        progress: "0",
      },
      {
        id: "2.8",
        task: "Etablissement du Planning",
        description: "Création du planning détaillé avec jalons et livrables",
        dependencies: "",
        startDate: "",
        duration: "22",
        progress: "0",
      },
      {
        id: "2.9",
        task: "Etablissement Plan de Communication",
        description:
          "Définition du plan de communication projet et parties prenantes",
        dependencies: "3.1",
        startDate: "",
        duration: "22",
        progress: "0",
      },
      {
        id: "2.10",
        task: "Approbation Cahier des charges,Plan de communication, Plan de projet et Planning",
        description: "Validation finale des documents de planification",
        dependencies: "3.2",
        startDate: "",
        duration: "1",
        progress: "0",
      },
      {
        id: "3",
        task: "Réalisation",
        description: "Phase de réalisation et développement du projet",
        dependencies: "4",
        startDate: "",
        duration: "",
        progress: "0",
      },
      {
        id: "3.1",
        task: "Paramétrage et tests unitaires",
        description:
          "Configuration du système et réalisation des tests unitaires",
        dependencies: "4.8, 4.9",
        startDate: "",
        duration: "65",
        progress: "0",
      },
      {
        id: "3.2",
        task: "Paramétrage et corrections Master Data",
        description: "Configuration des données de référence et corrections",
        dependencies: "4.3",
        startDate: "",
        duration: "65",
        progress: "0",
      },
      {
        id: "3.3",
        task: "Paramétrage, programmation et test Production",
        description: "Développement et tests pour le module production",
        dependencies: "4.6",
        startDate: "",
        duration: "65",
        progress: "0",
      },
      {
        id: "3.4",
        task: "Tests, corrections et validation du système",
        description: "Tests d'intégration et validation globale du système",
        dependencies: "4.7",
        startDate: "",
        duration: "20",
        progress: "0",
      },
      {
        id: "3.5",
        task: "Validation officielle par les utilisateurs",
        description: "Acceptation utilisateur finale (UAT)",
        dependencies: "4.5",
        startDate: "",
        duration: "1",
        progress: "0",
      },
      {
        id: "3.6",
        task: "Validation de la phase par le comité de direction",
        description:
          "Validation de la phase réalisation par le comité de pilotage",
        dependencies: "4.11",
        startDate: "",
        duration: "1",
        progress: "0",
      },
      {
        id: "4",
        task: "Contrôle et suivi",
        description: "Phase de contrôle qualité et suivi du déploiement",
        dependencies: "5",
        startDate: "",
        duration: "",
        progress: "0",
      },
      {
        id: "4.1",
        task: "Tests d'intégration et de consolidation",
        description: "Tests finaux d'intégration entre tous les modules",
        dependencies: "",
        startDate: "",
        duration: "21",
        progress: "0",
      },
      {
        id: "4.2",
        task: "Tests d'intégration et consolidation, validation du système",
        description: "Validation complète du système intégré",
        dependencies: "",
        startDate: "",
        duration: "10",
        progress: "0",
      },
      {
        id: "4.3",
        task: "Formation des utilisateurs",
        description: "Formation des équipes sur le nouveau système",
        dependencies: "",
        startDate: "",
        duration: "11",
        progress: "0",
      },
      {
        id: "4.4",
        task: "Préparation et validation environnement pré-productif",
        description: "Configuration et validation de l'environnement de test",
        dependencies: "",
        startDate: "",
        duration: "20",
        progress: "0",
      },
      {
        id: "4.5",
        task: "Déploiement",
        description: "Déploiement en production du nouveau système",
        dependencies: "",
        startDate: "",
        duration: "0",
        progress: "0",
      },
      {
        id: "4.6",
        task: "Basculement et validation",
        description: "Basculement vers le nouveau système et validation",
        dependencies: "",
        startDate: "",
        duration: "0",
        progress: "0",
      },
      {
        id: "4.7",
        task: "Validation Master Data",
        description: "Validation finale des données de référence",
        dependencies: "",
        startDate: "",
        duration: "0",
        progress: "0",
      },
      {
        id: "4.8",
        task: "Validation par les utilisateurs",
        description: "Validation finale par les utilisateurs métier",
        dependencies: "",
        startDate: "",
        duration: "0",
        progress: "0",
      },
      {
        id: "4.9",
        task: "Décision de démarrage productif",
        description: "Décision formelle de démarrage en production",
        dependencies: "",
        startDate: "",
        duration: "0",
        progress: "0",
      },
      {
        id: "4.10",
        task: "Décision de démarrage productif",
        description: "Confirmation de la décision de démarrage",
        dependencies: "",
        startDate: "",
        duration: "0",
        progress: "0",
      },
      {
        id: "4.11",
        task: "Suivi bon fonctionnement, documentation et corrections",
        description: "Support post-déploiement et corrections",
        dependencies: "",
        startDate: "",
        duration: "38",
        progress: "0",
      },
      {
        id: "4.12",
        task: "Corrections Master Data",
        description: "Corrections des données de référence en production",
        dependencies: "",
        startDate: "",
        duration: "38",
        progress: "0",
      },
      {
        id: "4.13",
        task: "Suivi par les utilisateurs",
        description: "Accompagnement des utilisateurs en phase de démarrage",
        dependencies: "",
        startDate: "",
        duration: "38",
        progress: "0",
      },
      {
        id: "4.14",
        task: "Clôture mensuelle",
        description: "Processus de clôture mensuelle avec le nouveau système",
        dependencies: "",
        startDate: "",
        duration: "4",
        progress: "0",
      },
      {
        id: "4.15",
        task: "PV Accusé de réception",
        description: "Rédaction du procès-verbal de réception définitive",
        dependencies: "",
        startDate: "",
        duration: "4",
        progress: "0",
      },
      {
        id: "5",
        task: "Clôture",
        description: "Phase de clôture formelle du projet",
        dependencies: "",
        startDate: "",
        duration: "",
        progress: "0",
      },
      {
        id: "5.1",
        task: "Liste des corrections",
        description: "Recensement final des corrections à effectuer",
        dependencies: "",
        startDate: "",
        duration: "10",
        progress: "0",
      },
      {
        id: "5.2",
        task: "Documentation leçons apprises Projet",
        description: "Documentation des leçons apprises et best practices",
        dependencies: "",
        startDate: "",
        duration: "10",
        progress: "0",
      },
      {
        id: "5.3",
        task: "Plan de corrections",
        description: "Planification des corrections futures et maintenance",
        dependencies: "",
        startDate: "",
        duration: "10",
        progress: "0",
      },
      {
        id: "5.4",
        task: "Réunion de clôture",
        description:
          "Réunion finale de clôture du projet avec toutes les parties prenantes",
        dependencies: "",
        startDate: "",
        duration: "0",
        progress: "0",
      },
    ];

    sampleTasks.forEach((t) => {
      const row = worksheet.addRow({
        id: t.id,
        task: t.task,
        description: t.description,
        dependencies: t.dependencies,
        startDate: t.startDate,
        duration: t.duration,
        status: "Non commencé",
        progress: t.progress,
        milestone: "false",
      });

      const rowNumber = row.number;
      const startDateCell = worksheet.getCell(`E${rowNumber}`);
      startDateCell.numFmt = "dd/mm/yyyy";
      const endDateCell = worksheet.getCell(`G${rowNumber}`);
      endDateCell.numFmt = "dd/mm/yyyy";

      if (!t.id.includes(".")) {
        row.eachCell({ includeEmpty: true }, (cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFC6E0B4" },
          };
          cell.font = { bold: true };
        });
      }
    });

    const headerRow = worksheet.getRow(1);
    headerRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF68C898" },
      };
      cell.font = {
        color: { argb: "FFFFFFFF" },
        bold: true,
      };
    });

    for (let i = 2; i <= 1000; i++) {
      worksheet.getCell(`I${i}`).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: ['"Non commencé,En cours,Terminé"'],
      };
      worksheet.getCell(`K${i}`).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: ['"true,false"'],
      };
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sample_tasks.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  const projects = useProjectQuery();
  const user = useUser();
  const isCollaborator = () => {
    const project = projects.data?.data.data.find(
      (p) => p.projectId == props.projectId,
    );
    if (
      user.user?.userOrganisation[0]?.role == UserRoleEnumValue.ADMINISTRATOR
    ) {
      return true;
    } else if (
      project?.assignedUsers.find(
        (u) =>
          u.user.userId == user.user?.userId &&
          u.projectRole == UserRoleEnumValue.PROJECT_MANAGER,
      )
    ) {
      return true;
    } else if (
      project?.assignedUsers.find(
        (u) =>
          (u.user.userId == user.user?.userId &&
            u.projectRole == UserRoleEnumValue.COLLABORATOR) ||
          u.projectRole == UserRoleEnumValue.TEAM_MEMBER,
      )
    ) {
      return false;
    } else {
      return false;
    }
  };
  return (
    <div className="">
      {!props.iconOnly && (
        <label className={labelStyle}>{t("Import your WBS")}</label>
      )}
      {wbsImportMutation.isPending && (
        <Loader className="absolute top-0 left-0 w-full h-full" />
      )}
      <input
        onChange={uploadCsv}
        type="file"
        accept=".xlsx"
        className="hidden"
        ref={csvUplodeRef}
      />
      {props.iconOnly ? (
        <div className="ml-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="cursor-pointer">
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <div className="flex w-7">
                        <img src={ExcelIcon} className="w-4 min-w-4" />
                        <img
                          src={DownLoadIcon}
                          className="w-2 min-w-2 mt-2 rotate-180"
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-sm">{t("import more task")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-auto">
              {isCollaborator() && (
                <DropdownMenuItem>
                  <Button
                    isLoading={wbsImportMutation.isPending}
                    disabled={wbsImportMutation.isPending}
                    variant={"link"}
                    onClick={() => csvUplodeRef.current?.click()}
                    className="w-full  py-5 mt-0.5 text-gray-600"
                  >
                    <img
                      src={ExcelIcon}
                      className="mr-1 brightness-0  w-4 min-w-4"
                    />{" "}
                    {t("Upload a csv file")}
                  </Button>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem>
                <Button
                  variant={"link"}
                  className="w-fit text-gray-600 justify-start"
                  type="button"
                  onClick={() => downloadCsv()}
                >
                  <div className="flex gap-2 ">
                    <img className="h-4 w-4" src={DownLoadIcon} />{" "}
                    {t("Download_sample_File")}
                  </div>
                </Button>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : (
        <div>
          {isCollaborator() && (
            <Button
              isLoading={wbsImportMutation.isPending}
              disabled={wbsImportMutation.isPending}
              variant={"outline"}
              onClick={() => csvUplodeRef.current?.click()}
              className="w-full border-2 py-5 mt-0.5 text-gray-600"
            >
              <div className="flex gap-1 items-center">
                <img src={ExcelIcon} className="brightness-0  w-4 min-w-4" />{" "}
                {t("Upload a csv file")}
              </div>
            </Button>
          )}
          <Button
            variant={"link"}
            className="w-fit text-gray-600 justify-start"
            type="button"
            onClick={() => downloadCsv()}
          >
            <div className="flex gap-2 ">
              <img className="h-4 w-4" src={DownLoadIcon} />{" "}
              {t("Download_sample_File")}
            </div>
          </Button>
        </div>
      )}
    </div>
  );
}

export default WbsImport;
